/** Formatting and date helpers shared by the server (reports, CSV) and the web app. */
export function formatMinutes(totalMinutes) {
    const minutes = Math.max(0, Math.round(totalMinutes));
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    return `${hours}h ${String(rest).padStart(2, '0')}m`;
}
export function formatDuration(totalSeconds) {
    const seconds = Math.max(0, Math.round(totalSeconds));
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const rest = seconds % 60;
    return [hours, minutes, rest].map((part) => String(part).padStart(2, '0')).join(':');
}
/** `YYYY-MM-DD` in the given IANA time zone (defaults to the runtime zone). */
export function toDateKey(date, timeZone) {
    if (!timeZone) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(date);
    return parts;
}
export function parseDateKey(key) {
    const [year, month, day] = key.split('-').map(Number);
    return new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, day ?? 1));
}
/** Monday-based start of the ISO week containing `date`. */
export function startOfWeek(date) {
    const copy = new Date(date);
    const day = copy.getDay();
    const diff = (day + 6) % 7;
    copy.setDate(copy.getDate() - diff);
    copy.setHours(0, 0, 0, 0);
    return copy;
}
export function startOfDay(date) {
    const copy = new Date(date);
    copy.setHours(0, 0, 0, 0);
    return copy;
}
export function endOfDay(date) {
    const copy = new Date(date);
    copy.setHours(23, 59, 59, 999);
    return copy;
}
export function addDays(date, days) {
    const copy = new Date(date);
    copy.setDate(copy.getDate() + days);
    return copy;
}
export function eachDateKey(start, end) {
    const keys = [];
    let cursor = parseDateKey(start);
    const last = parseDateKey(end);
    while (cursor.getTime() <= last.getTime()) {
        keys.push(toDateKey(cursor, 'UTC'));
        cursor = new Date(cursor.getTime() + 86_400_000);
    }
    return keys;
}
/** Saturday/Sunday. Business-day rules stay configurable — see BRD §11 Q6. */
export function isWeekend(date) {
    const day = date.getDay();
    return day === 0 || day === 6;
}
export function countBusinessDays(start, end) {
    let count = 0;
    let cursor = startOfDay(start);
    const last = startOfDay(end);
    while (cursor.getTime() <= last.getTime()) {
        if (!isWeekend(cursor))
            count += 1;
        cursor = addDays(cursor, 1);
    }
    return count;
}
export function relativeTime(iso, now = new Date()) {
    const then = new Date(iso).getTime();
    const diffSeconds = Math.round((now.getTime() - then) / 1000);
    if (Number.isNaN(diffSeconds))
        return '';
    const abs = Math.abs(diffSeconds);
    if (abs < 45)
        return 'just now';
    if (abs < 90)
        return diffSeconds > 0 ? 'a minute ago' : 'in a minute';
    const minutes = Math.round(abs / 60);
    if (minutes < 60)
        return diffSeconds > 0 ? `${minutes}m ago` : `in ${minutes}m`;
    const hours = Math.round(minutes / 60);
    if (hours < 24)
        return diffSeconds > 0 ? `${hours}h ago` : `in ${hours}h`;
    const days = Math.round(hours / 24);
    if (days < 30)
        return diffSeconds > 0 ? `${days}d ago` : `in ${days}d`;
    return new Date(iso).toLocaleDateString();
}
//# sourceMappingURL=time.js.map